import * as XLSX from 'xlsx';
import {
  blankAoaOutsideMergeOrigins,
  buildExcelMergeRanges,
  columnsHaveCellMerge,
} from './bodyMergeUtils';

/**
 * 그리드 우클릭 빠른 내보내기 — 컬럼 선택 UI 없이 전체 조회 데이터 다운로드
 */

function normalizeHeader(header, field) {
  return String(header ?? field ?? '').replace(/^\*\s*/, '');
}

/**
 * @param {Array} dataColumns - HiGrid dataColumns (mod/rowNumber 제외된 정의)
 * @returns {{ field: string, header: string }[]}
 */
export function resolveExportColumns(dataColumns = []) {
  return (dataColumns || [])
    .filter((c) => c && c.visible !== false && c.meta?.visible !== false)
    .map((c) => {
      const field = c.accessorKey || c.id || c.field || '';
      return {
        field,
        header: normalizeHeader(c.header, field),
      };
    })
    .filter((c) => c.field && c.field !== 'mod' && c.field !== 'rowNumber' && c.field !== 'rowCheck');
}

/**
 * @param {object[]} rows - 행 데이터 (original objects)
 * @param {Array} dataColumns
 * @returns {any[][]}
 */
export function buildGridExportAoa(rows, dataColumns) {
  const cols = resolveExportColumns(dataColumns);
  const aoa = [cols.map((c) => c.header)];
  for (const row of rows || []) {
    aoa.push(cols.map((c) => {
      const v = row?.[c.field];
      if (v == null || v === '') return '';
      if (typeof v === 'object' && v instanceof Date) return v.toISOString().slice(0, 10);
      return v;
    }));
  }
  return aoa;
}

function withExt(fileName, ext) {
  const base = String(fileName || 'grid_export').replace(/\.(xlsx|csv)$/i, '');
  return `${base}.${ext}`;
}

/**
 * @param {object[]} rows
 * @param {Array} dataColumns
 * @param {string} [fileName]
 * @param {{ includeMerged?: boolean, includeUnmerged?: boolean }} [options]
 *   - 본문 셀 병합 컬럼이 있으면 기본: 병합해제 + 셀병합 시트 모두 포함
 */
export function downloadGridXlsx(rows, dataColumns, fileName = 'grid_export', options = {}) {
  const hasMerge = columnsHaveCellMerge(dataColumns);
  const includeUnmerged = options.includeUnmerged !== false;
  const includeMerged = options.includeMerged !== false && hasMerge;

  const aoaRaw = buildGridExportAoa(rows, dataColumns);
  const exportFields = resolveExportColumns(dataColumns).map((c) => c.field);
  const sheets = [];

  if (includeUnmerged) {
    sheets.push({
      name: hasMerge ? '병합해제' : 'Sheet1',
      aoa: aoaRaw,
    });
  }

  if (includeMerged) {
    const merges = buildExcelMergeRanges(rows, dataColumns, exportFields);
    sheets.push({
      name: '셀병합',
      aoa: merges.length
        ? blankAoaOutsideMergeOrigins(aoaRaw.map((row) => row.slice()), merges)
        : aoaRaw.map((row) => row.slice()),
      merges,
    });
  }

  if (!sheets.length) {
    sheets.push({ name: 'Sheet1', aoa: aoaRaw });
  }

  const wb = XLSX.utils.book_new();
  const usedNames = new Set();
  sheets.forEach((sheet, idx) => {
    const ws = XLSX.utils.aoa_to_sheet(sheet.aoa);
    if (sheet.merges?.length) ws['!merges'] = sheet.merges;
    let name = String(sheet.name || `Sheet${idx + 1}`).slice(0, 31);
    if (usedNames.has(name)) name = `${name.slice(0, 28)}_${idx + 1}`;
    usedNames.add(name);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, withExt(fileName, 'xlsx'));
}

export function downloadGridCsv(rows, dataColumns, fileName = 'grid_export') {
  // CSV는 셀 병합 불가 — 병합 해제(원본) 데이터만
  const aoa = buildGridExportAoa(rows, dataColumns);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = withExt(fileName, 'csv');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
